import Config from 'Config';
import xhr from 'xhr';

import Cookies from 'lib/Cookies';
import userAgent from 'lib/currentUserAgent';
import { RouterState } from 'lib/ReactRouterTypes';
import uuid from 'uuid/v4';
import { EventsDispatcher } from 'lib/EventsDispatcher';
import { isE2ETest } from 'lib/e2eTest';

function decamelizeProperties(properties: { [key: string]: any }) {
  const props: { [key: string]: any } = {};
  for (const key in properties) {
    props[key.replace(/([a-z\d])([A-Z])/g, '$1_$2').toLowerCase()] = properties[key];
  }
  return props;
}

const windowDefined = typeof window !== 'undefined';

export const ref = { xhr }; // tests :(

export type EventData = {
  event: string;
  timestamp: string;
  properties: { [key: string]: any };
  client_type?: string;
  client_uuid?: string;
  foreign_service?: string;
  request_url?: string;
  screen_width?: number;
  screen_height?: number;
  session_uuid?: string;
};

export class EventTracking {
  userId?: number | null;
  userUuid?: string | null;
  dispatcher: EventsDispatcher;

  constructor() {
    this.userId = null;
    this.userUuid = null;
    this.dispatcher = new EventsDispatcher();

    if (windowDefined) {
      this.addSessionProperties({
        client_segment: userAgent.clientSegment(),
        client_type: 'Web',
        client_uuid: this.getClientId(),
        foreign_service: 'r101-web-client',
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        session_uuid: this.getSessionId(),
        client_version: Config.Environment.EMPIRE_RELEASE,
      });
    }
  }

  addSessionProperties(newSessionProperties: {}) {
    this.dispatcher.addSessionProperties(newSessionProperties);
  }

  setUserInfo(userId?: number, userUuid?: string) {
    this.userId = userId;
    this.userUuid = userUuid;
  }

  /**
   * SessionId uniquely identifies a session. A session is
   * begins when a user hits remind.com, and terminates when
   * the browser closes or the user logs out.
   *
   * I.e. this is a *usage* session *not an auth* session.
   */
  getSessionId(): string | undefined {
    if (!windowDefined) {
      return undefined;
    }

    const storedSessionId = Cookies.get('session_uuid');
    if (storedSessionId) {
      return storedSessionId;
    }

    const freshSessionId = uuid();
    Cookies.set('session_uuid', freshSessionId);
    return freshSessionId;
  }

  getClientId(): string | undefined {
    if (windowDefined) {
      let clientId = Cookies.get('client_uuid');
      if (!clientId) {
        Cookies.set('client_uuid', (clientId = uuid()));
      }
      return clientId;
    }
    return undefined;
  }

  // Used internally - generally use trackKey for ui tracking.
  track(name: string, input_properties: any = {}) {
    const properties = { ...input_properties };

    properties['user_id'] = this.userId;
    properties['user_uuid'] = this.userUuid;
    if (windowDefined) {
      try {
        properties['request_url'] = window.location.href;
      } catch (err) {
        // this sometimes fails in test environment
      }
    }
    const timestamp = new Date().toISOString();

    // Skip sending events
    if (isE2ETest()) {
      console.log(`EVENT: ${name} (${timestamp}) - ${JSON.stringify(properties)}`);
      return;
    }

    this.dispatcher.enqueue({
      event: name,
      properties,
      timestamp,
    });
  }

  trackKey(
    key: string,
    properties: {
      [key: string]: any;
    } = {}
  ) {
    this.track(
      'ui.event',
      Object.assign(
        {
          event_key: key,
        },
        decamelizeProperties(properties)
      )
    );
  }

  // FIXME contextName should be string but bad args are coming in
  tap(screenName: string, elementName: string, contextName: any = null, properties: Object = {}) {
    this.track(
      'ui.tapped',
      Object.assign(
        {
          screen_name: screenName,
          ui_element_name: elementName,
          ui_context: contextName,
        },
        decamelizeProperties(properties)
      )
    );
  }

  view(screenName: string | undefined, properties: Object = {}) {
    if (screenName) {
      this.track(
        'ui.viewed',
        Object.assign(
          {
            screen_name: screenName,
          },
          decamelizeProperties(properties)
        )
      );
    }
  }

  request(screenName: string, properties: Object = {}) {
    this.track(
      'ui.requested',
      Object.assign(
        {
          screen_name: screenName,
        },
        decamelizeProperties(properties)
      )
    );
  }

  experimentExposure(experiment: string, variant: string, properties: Object = {}) {
    this.track(
      'experiment.exposure',
      Object.assign(
        {
          experiment,
          alternative: variant,
        },
        decamelizeProperties(properties)
      )
    );
  }
}

export default new EventTracking();

const stripHOCNames = (name: string) => {
  const thingsToRemove = [
    'Apollo',
    'awaitData',
    'FluxContainer',
    'Connect',
    'WaitForPromises',
    'TrackView',
    'withCookies',
    'withUserAgent',
    'withLayerHideOnEscapeTrigger',
    'withLayerHideOnTransitionTrigger',
    'withLayerAutoFocus',
    'withLayerHideOnBlurTrigger',
    'withDateTimePickerPosition',
    'withWidth',
    /[()-]/g,
  ];
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'replace' does not exist on type 'string ... Remove this comment to see the full error message
  return thingsToRemove.reduce((res, thing) => res.replace(thing, ''), name);
};

export const getEventScreenName = (
  props:
    | {
        [key: string]: any;
      }
    | undefined,
  router: RouterState | undefined
): string | null => {
  if (props && props.eventScreenName) {
    return props.eventScreenName;
  }
  if (router) {
    const innermostRouteHandlerName = router.routes.reduce((prevName: any, route: any) => {
      if (route.component) {
        const { name, displayName } = route.component;
        return displayName || name || prevName;
      } else {
        return prevName;
      }
    }, '');

    if (innermostRouteHandlerName) {
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | RegExp' is not assignable to type '... Remove this comment to see the full error message
      return stripHOCNames(innermostRouteHandlerName);
    }
  }
  return null;
};

export const getEventElementName = (props: { [key: string]: any }) => {
  return props.eventElementName || null;
};

export const getEventContextName = (
  props: {
    [key: string]: any;
  },
  context: {
    [key: string]: any;
  }
) => {
  if (props.eventContextName) {
    return props.eventContextName;
  }

  if (context.eventContextName) {
    return context.eventContextName;
  }

  return null;
};

export const getEventProperties = (
  eventProperties:
    | {
        [key: string]: any;
      }
    | undefined,
  context: {
    [key: string]: any;
  }
) => {
  return Object.assign(context.eventProperties || {}, eventProperties || {});
};
